import { cn } from "@faire/prototype-playground/lib/utils";
import type { WizardImageSlot } from "@faire/prototype-playground/workspace/yz/spe-ai-assisted-flow-mock/MockSpeImagesGrid";
import { IconButton } from "@faire/slate/components/icon-button";
import { TextInput } from "@faire/slate/components/text-input";
import { Core } from "@faire/slate/foundation/color";
import { Close } from "@faire/slate-icons/Close";
import { ImageLandscape } from "@faire/slate-icons/ImageLandscape";
import { StarFilled } from "@faire/slate-icons/StarFilled";
import { Upload } from "@faire/slate-icons/Upload";
import { Star } from "@faire/web/slate/icons/foundation/Star";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import React, { useCallback, useRef } from "react";

import { INLINE_SPE_COPY } from "./inlineSpeCopy";

const PLACEHOLDER_SLOT_COUNT = 8;

const slotShellClass =
  "border-fs-border-muted bg-fs-surface-primary relative flex min-h-0 items-center justify-center overflow-hidden rounded-[var(--slate-radius-component-default)] border";

type InlineSpeImagesGridProps = {
  images: WizardImageSlot[];
  onChange: (next: WizardImageSlot[]) => void;
};

/**
 * SPE Images section: featured upload (left) + 8 rectangular placeholder slots (right).
 * Drag-and-drop reorder matches wizard `MockSpeImagesGrid` (options table uses frozen URLs).
 */
export const InlineSpeImagesGrid: React.FC<InlineSpeImagesGridProps> = ({
  images,
  onChange,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragIndex = useRef<number | null>(null);

  const appendFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList?.length) {
        return;
      }
      const next = [...images];
      for (const file of Array.from(fileList)) {
        if (!file.type.startsWith("image/")) {
          continue;
        }
        next.push({
          id: `inline-img-${crypto.randomUUID()}`,
          url: URL.createObjectURL(file),
        });
      }
      onChange(next);
    },
    [images, onChange]
  );

  const removeAt = useCallback(
    (index: number) => {
      const removed = images[index];
      if (!removed) {
        return;
      }
      if (removed.url.startsWith("blob:")) {
        URL.revokeObjectURL(removed.url);
      }
      onChange(images.filter((_, i) => i !== index));
    },
    [images, onChange]
  );

  const reorderToSlot = useCallback(
    (from: number, dropIndex: number) => {
      if (
        from === dropIndex ||
        from < 0 ||
        dropIndex < 0 ||
        from >= images.length
      ) {
        return;
      }
      const next = [...images];
      const [item] = next.splice(from, 1);
      let insertIndex = dropIndex;
      if (from < dropIndex) {
        insertIndex -= 1;
      }
      next.splice(insertIndex, 0, item);
      onChange(next);
    },
    [images, onChange]
  );

  const promoteToFeatured = useCallback(
    (index: number) => {
      if (index <= 0) {
        return;
      }
      reorderToSlot(index, 0);
    },
    [reorderToSlot]
  );

  const openPicker = () => {
    fileInputRef.current?.click();
  };

  const onDragStart = (index: number) => (e: React.DragEvent) => {
    dragIndex.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const onDragEnd = () => {
    dragIndex.current = null;
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragIndex.current !== null ? "move" : "copy";
  };

  const onDropOnSlot = (targetIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) {
      appendFiles(e.dataTransfer.files);
      dragIndex.current = null;
      return;
    }
    const from = dragIndex.current;
    dragIndex.current = null;
    if (from === null) {
      return;
    }
    reorderToSlot(from, targetIndex);
  };

  const featuredImage = images[0];

  const renderFilledTile = (
    img: WizardImageSlot,
    index: number,
    className?: string
  ) => (
    <div
      key={img.id}
      className={cn(
        slotShellClass,
        "group/tile relative cursor-grab active:cursor-grabbing",
        className
      )}
      style={{ height: "auto" }}
      draggable
      onDragStart={onDragStart(index)}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDropOnSlot(index)}
    >
      <img
        src={img.url}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        draggable={false}
      />
      {index === 0 ? (
        <div className="pointer-events-none absolute bottom-2 left-2 z-[1] flex items-center gap-1 rounded bg-[color-mix(in_srgb,var(--slate-color-surface-primary-inverse)_80%,transparent)] px-2 py-1">
          <StarFilled
            style={{
              width: 12,
              height: 12,
              color: "var(--slate-color-icon-primary-inverse)",
            }}
          />
          <span className="type-fs-label-medium text-[var(--slate-color-text-primary-inverse)]">
            Featured
          </span>
        </div>
      ) : null}
      {index > 0 ? (
        <button
          type="button"
          className="border-fs-border-default absolute right-2 bottom-2 z-[1] flex size-6 cursor-pointer items-center justify-center rounded-full bg-[color-mix(in_srgb,var(--slate-color-surface-primary-inverse)_80%,transparent)] opacity-100 md:opacity-0 md:group-hover/tile:opacity-100"
          aria-label="Set as featured image"
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={(e) => {
            e.stopPropagation();
            promoteToFeatured(index);
          }}
        >
          <Star
            style={{
              width: 12,
              height: 12,
              color: "var(--slate-color-icon-primary-inverse)",
            }}
          />
        </button>
      ) : null}
      <div className="absolute top-1 right-1 z-[1]">
        <IconButton
          variant="secondary"
          size="xxSmall"
          shape="square"
          Icon={Close}
          aria-label="Remove image"
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          onClick={() => {
            removeAt(index);
          }}
        />
      </div>
    </div>
  );

  const renderEmptyPlaceholder = (slotKey: string, targetIndex: number) => (
    <div
      key={slotKey}
      className={cn(slotShellClass, "aspect-[3/4] min-h-[72px] w-full")}
      onDragOver={onDragOver}
      onDrop={onDropOnSlot(targetIndex)}
    >
      <ImageLandscape
        style={{
          width: 24,
          height: 24,
          color: "var(--slate-color-icon-subdued)",
        }}
        aria-hidden
      />
    </div>
  );

  return (
    <Column>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={(e) => {
          appendFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <div
        className="flex w-full flex-row items-stretch gap-3"
        onDragOver={onDragOver}
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer.files?.length) {
            appendFiles(e.dataTransfer.files);
            dragIndex.current = null;
          }
        }}
      >
        {featuredImage ? (
          renderFilledTile(
            featuredImage,
            0,
            "aspect-[3/4] h-full w-[280px] shrink-0"
          )
        ) : (
          <button
            type="button"
            className={cn(
              slotShellClass,
              "border-fs-border-muted text-fs-text-subdued hover:border-fs-border-default flex aspect-[3/4] w-[280px] shrink-0 cursor-pointer flex-col items-center justify-center gap-2 border-dashed bg-[var(--slate-color-surface-secondary)] p-4 text-center"
            )}
            onClick={openPicker}
            onDragOver={onDragOver}
            onDrop={onDropOnSlot(0)}
          >
            <div className="border-fs-border-muted flex size-[60px] items-center justify-center rounded-full border">
              <Upload
                style={{
                  width: 28,
                  height: 28,
                  color: "var(--slate-color-icon-subdued)",
                }}
              />
            </div>
            <Typography variant="paragraphSansMedium" color={Core.text.primary}>
              {INLINE_SPE_COPY.uploadFeaturedLabel}
            </Typography>
            <Typography variant="labelSansRegular" color={Core.text.subdued}>
              {INLINE_SPE_COPY.uploadFeaturedFormats}
            </Typography>
            <Typography variant="labelSansRegular" color={Core.text.subdued}>
              {INLINE_SPE_COPY.uploadFeaturedMinSize}
            </Typography>
          </button>
        )}

        <div className="grid min-h-0 min-w-0 flex-1 grid-cols-4 grid-rows-2 gap-3">
          {Array.from({ length: PLACEHOLDER_SLOT_COUNT }, (_, slotIndex) => {
            const imageIndex = slotIndex + 1;
            const img = images[imageIndex];
            if (img) {
              return renderFilledTile(
                img,
                imageIndex,
                "aspect-[3/4] w-full min-h-[72px]"
              );
            }
            return renderEmptyPlaceholder(
              `placeholder-${slotIndex}`,
              imageIndex
            );
          })}
        </div>
      </div>

      <Spacer size="6x" />
      <Typography variant="labelSansRegular" color={Core.text.subdued}>
        {INLINE_SPE_COPY.imagesUploadHint}
      </Typography>
      <Spacer size="2x" />
      <div
        onPaste={(e) => {
          const items = e.clipboardData?.files;
          if (items?.length) {
            e.preventDefault();
            appendFiles(items);
          }
        }}
      >
        <TextInput placeholder={INLINE_SPE_COPY.imagesPastePlaceholder} />
      </div>
    </Column>
  );
};
